/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NotificationInterface } from 'medicalsystem-shared';
import { getFromISOISOYYYYMMDDTHHMMSSTimeFormat } from "utils/date.utils";

/**
 * Redux state for managing system notifications with read/unread tracking.
 */
export type NotificationState = {
  notifications: NotificationInterface[];
  lastUpdated: string;
  initialized: boolean;
  unreadCount: number;
  readCount: number;
  totalCount: number;
};

const initialState: NotificationState = {
  notifications: [],
  lastUpdated: getFromISOISOYYYYMMDDTHHMMSSTimeFormat(new Date()),
  initialized: false,
  unreadCount: 0,
  readCount: 0,
  totalCount: 0,
};

/**
 * Calculates notification counts (unread, read, total) from notification array.
 * @param notifications - Array of notification objects
 * @returns Object with unreadCount, readCount, and totalCount
 */
const calculateCounts = (notifications: NotificationInterface[]) => {
  const safeNotifications = Array.isArray(notifications) ? notifications : [];
  const unreadCount = safeNotifications.filter(n => n && !n.isRead).length;
  const readCount = safeNotifications.filter(n => n && n.isRead).length;
  const totalCount = safeNotifications.length;
  
  return { unreadCount, readCount, totalCount };
};

const updateCounts = (state: NotificationState) => {
  const counts = calculateCounts(state.notifications);
  state.unreadCount = counts.unreadCount;
  state.readCount = counts.readCount;
  state.totalCount = counts.totalCount;
};

/**
 * Sets the complete notification list and updates counts.
 * Marks state as initialized after first load.
 * @param state - Current notification state
 * @param action - Payload containing array of notifications
 */
const setNotifications = (state: NotificationState, action: PayloadAction<NotificationInterface[]>) => {
  const safeNotifications = Array.isArray(action.payload) ? action.payload : [];
  state.notifications = safeNotifications;
  state.lastUpdated = getFromISOISOYYYYMMDDTHHMMSSTimeFormat(new Date());
  state.initialized = true;
  updateCounts(state);
};

const updateNotification = (state: NotificationState, action: PayloadAction<NotificationInterface>) => {
  const index = state.notifications.findIndex(n => n.id === action.payload.id);
  if (index !== -1) {
    state.notifications[index] = action.payload;
    updateCounts(state);
  }
};

/**
 * Marks a single notification as read by ID and updates counts.
 * @param state - Current notification state
 * @param action - Payload containing notification ID
 */
const markAsRead = (state: NotificationState, action: PayloadAction<string>) => {
  const notification = state.notifications.find(n => n.id === action.payload);
  if (notification && !notification.isRead) {
    notification.isRead = true;
    updateCounts(state);
  }
};

/**
 * Marks all notifications as read and updates counts.
 * @param state - Current notification state
 */
const markAllAsRead = (state: NotificationState) => {
  state.notifications.forEach(notification => {
    notification.isRead = true;
  });
  updateCounts(state);
};

const markMultipleAsRead = (state: NotificationState, action: PayloadAction<string[]>) => {
  const notificationIds = action.payload;
  let hasChanges = false;
  
  state.notifications.forEach(notification => {
    if (notificationIds.includes(notification.id) && !notification.isRead) {
      notification.isRead = true;
      hasChanges = true;
    }
  });

  if (hasChanges) {
    updateCounts(state);
  }
};

const addNotification = (state: NotificationState, action: PayloadAction<NotificationInterface>) => {
  const exists = state.notifications.some(n => n.id === action.payload.id);
  if (!exists) {
    state.notifications.unshift(action.payload);
    updateCounts(state);
  }
};

const removeNotification = (state: NotificationState, action: PayloadAction<string>) => {
  const index = state.notifications.findIndex(n => n.id === action.payload);
  if (index !== -1) {
    state.notifications.splice(index, 1);
    updateCounts(state);
  }
};

const resetNotifications = (state: NotificationState) => {
  state.notifications = [];
  state.lastUpdated = null;
  state.initialized = false;
  state.unreadCount = 0;
  state.readCount = 0;
  state.totalCount = 0;
};

const setInitialized = (state: NotificationState, action: PayloadAction<boolean>) => {
  state.initialized = action.payload;
};
const getNotificationCounts = (state: NotificationState) => ({
  unreadCount: state.unreadCount,
  readCount: state.readCount,
  totalCount: state.totalCount,
});

const NotificationSlice = createSlice({
  name: "notifications",
  initialState: initialState,
  reducers: {
    setNotifications: setNotifications,
    updateNotification: updateNotification,
    markAsRead: markAsRead,
    markAllAsRead: markAllAsRead,
    markMultipleAsRead: markMultipleAsRead,
    addNotification: addNotification,
    removeNotification: removeNotification,
    resetNotifications: resetNotifications,
    setInitialized: setInitialized,
  },
});

export const notificationActions = NotificationSlice.actions;
export const selectNotificationCounts = (state: { notificationAction: NotificationState }) => 
  getNotificationCounts(state.notificationAction);

export const selectUnreadCount = (state: { notificationAction: NotificationState }) => 
  state.notificationAction.unreadCount;

export const selectReadCount = (state: { notificationAction: NotificationState }) => 
  state.notificationAction.readCount;

export const selectTotalCount = (state: { notificationAction: NotificationState }) => 
  state.notificationAction.totalCount;

export default NotificationSlice;
